/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  AccordionHeader,
  AccordionBody,
  FormController,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  SelectionControlLabel,
  Typography
} from '@one-thd/sui-atomic-components';
import { ArrowUp, ArrowDown } from '@one-thd/sui-icons';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { ProductResultsContext } from './ProductResultsContext';
import prZipCodes from '../maPuertoRicoZipCodes';
import {
  browseSortTypes,
  searchSortTypes
} from '../utils/constants';
import {
  getSortNameValue,
  getIsMajorAppliance,
  manageDeliverySpeedSort
} from '../product-results-helpers';
import {
  shouldShowSortPopover,
  handleSortSelection,
  shouldRenderPopover,
  handlePopoverCloseEvent
} from '../utils/sortPopoverUtils';
import { getFirstNonEmptyObject } from '../utils/searchHelpers';
import { usePopoverManager } from '../hooks/usePopoverManager';

const { func, bool } = PropTypes;

const ResultsSortBy = ({
  drawer, onSortChange, oldDesign, isMobile, searchModelData: propData
}) => {
  const isSortByDeliveryDateEnabled = useConfigService('fs-prop:sort-by-delivery-date-enabled') || false;
  const isSortByDeliverySpeedTileEnabled = useConfigService('fs-prop:sort-by-delivery-speed-tile-enabled') || false;
  const isSortPopoverEnabled = useConfigService('fs:isSortPopoverEnabled') || false;
  const [currentSort, setCurrentSort] = useState(null);
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);
  const { data: contextData } = useContext(ProductResultsContext);
  const data = getFirstNonEmptyObject(contextData, propData);
  const isMajorAppliance = getIsMajorAppliance(data);
  const { deliveryZip, channel } = useContext(ExperienceContext);
  const isZipCodeSuppressed = prZipCodes[Number(deliveryZip)] !== undefined;
  const showDeliverySpeed = isMajorAppliance && !isZipCodeSuppressed;

  const handleScroll = (event) => {
    setOpen(false);
  };

  const deliverySpeedSortType = {
    name: 'Delivery Speed',
    sortby: 'deliverydate',
    sortorder: 'asc'
  };

  const { searchReport } = data?.searchModel || {};

  const {
    startIndex = 0, pageSize = 0, keyword = '', sortBy = '', sortOrder = ''
  } = searchReport || {};

  const sortTypes = keyword ? searchSortTypes : browseSortTypes;

  const [sortValue, setSortValue] = useState(getSortNameValue({
    key: (sortBy && sortBy.toLowerCase()) || '',
    order: (sortOrder && sortOrder.toLowerCase()) || '',
    keyword
  }).value);

  const popover = usePopoverManager({
    enabled: isSortPopoverEnabled,
    shouldShow: shouldShowSortPopover,
    dropdownOpen: open,
    startIndex,
    initialDelay: 5000,
    restorationDelay: 1500
  });

  const handleDropdownClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
    popover.resetInteraction();
  };

  useEffect(() => {
    manageDeliverySpeedSort(
      browseSortTypes,
      showDeliverySpeed,
      isSortByDeliveryDateEnabled,
      deliverySpeedSortType
    );

    window.LIFE_CYCLE_EVENT_BUS.trigger('sortorder.value', {
      sortby: sortBy.toLowerCase(),
      sortorder: sortOrder.toLowerCase()
    });

    const currentSortValue = `${sortBy}:${sortOrder}`;
    if (currentSortValue !== sortValue) {
      setSortValue(getSortNameValue({
        key: (sortBy && sortBy.toLowerCase()) || '',
        order: (sortOrder && sortOrder.toLowerCase()) || ''
      }).value);
    }
  }, [showDeliverySpeed, browseSortTypes, isSortByDeliveryDateEnabled, searchReport, sortBy, sortOrder, sortValue]);

  const currentPage = startIndex && pageSize ? Math.round(startIndex / pageSize) + 1 : 1;
  const pageType = keyword ? 'search' : 'browse';
  const sortTypeConfig = { sortTypes, currentPage, pageType };

  useEffect(() => {
    const handleDeliverySpeedEvent = (event) => {
      const sortName = event?.output?.isDeliverySpeedSortSelected
        ? 'Delivery Speed'
        : sortTypeConfig.sortTypes[0].name;
      const sortByPosition = 'tile';
      handleSortSelection(sortName, onSortChange, sortTypeConfig, isSortPopoverEnabled, sortByPosition);
    };

    if (isSortByDeliverySpeedTileEnabled) {
      window.LIFE_CYCLE_EVENT_BUS?.on('sortorder.delivery-speed', handleDeliverySpeedEvent);
    }
    return () => {
      if (isSortByDeliverySpeedTileEnabled) {
        window.LIFE_CYCLE_EVENT_BUS?.off('sortorder.delivery-speed', handleDeliverySpeedEvent);
      }
    };
  }, [sortTypes, currentPage, pageType, onSortChange, isSortPopoverEnabled]);

  if (!data) return null;

  const shouldRenderPopoverComponent = isSortPopoverEnabled
    && shouldRenderPopover(popover.anchorEl, pageType, channel, showDeliverySpeed);

  const handleSortClick = (evt) => {
    const sortName = evt.currentTarget.textContent || evt.currentTarget.innerText;
    setCurrentSort(sortName);
    popover.handleDropdownInteraction();

    const result = handleSortSelection(sortName, onSortChange, sortTypeConfig, isSortPopoverEnabled);
    if (result.hidePopover && isSortPopoverEnabled) {
      popover.close();
    }
    setOpen(false);
  };

  const handleRadioChangeMobile = (event) => {
    if (!event?.target?.value) return;

    const [sortedby, sortedorder] = event.target.value.split(':');
    setSortValue(`${sortedby}:${sortedorder}`);

    const sortType = sortTypes.find(type => type.sortby === sortedby && type.sortorder === sortedorder);
    if (sortType) {
      handleSortSelection(sortType, onSortChange, sortTypeConfig, isSortPopoverEnabled);
    }
  };

  const handleRadioChange = (event) => {
    if (!event?.target?.value) return;

    const [sortby, sortorder] = event.target.value.split(':');
    setSortValue(`${sortby}:${sortorder}`);

    const sortType = sortTypes.find(type => type.sortby === sortby && type.sortorder === sortorder);
    if (sortType) {
      handleSortSelection(sortType, onSortChange, sortTypeConfig, isSortPopoverEnabled);
    }
  };

  const handleToggle = () => {
    setOpen((prevOpen) => {
      const willBeOpen = !prevOpen;

      if (isSortPopoverEnabled && willBeOpen) {
        popover.handleDropdownInteraction();
      }

      return willBeOpen;
    });
  };

  const handlePopoverClose = (event) => {
    popover.handleCloseEvent(event, handlePopoverCloseEvent);
  };

  const sortName = getSortNameValue({
    key: (sortBy && sortBy.toLowerCase()) || '',
    order: (sortOrder && sortOrder.toLowerCase()) || ''
  }).name;

  const generateSortByRadios = (radioButtons) => {
    return radioButtons.map((radioButton, index) => {
      const { name, sortby, sortorder } = radioButton;
      const isChecked = sortValue === `${sortby}:${sortorder}`;
      return (
        <div key={index}>
          <SelectionControlLabel label={name}>
            <Radio
              checked={isChecked}
              id={`${sortby}-${sortorder}-${index}`}
              onChange={isMobile && !oldDesign ? handleRadioChangeMobile : handleRadioChange}
              value={`${sortby}:${sortorder}`}
              name="sort-by-radio-buttons"
              inputAttributes={{ 'aria-label': name }}
            />
          </SelectionControlLabel>
        </div>
      );
    });
  };

  return (
    <>
      {!drawer && (
        <div data-component="ResultsSortBy" className="sui-flex sui-items-center sui-justify-end">
          <List disablePadding>
            <ListItemButton
              disableGutters
              ref={anchorRef}
              id="sort-menu-button"
              onClick={handleToggle}
              aria-expanded={open ? 'true' : undefined}
              aria-haspopup="listbox"
              ara-controls="sort-menu"
              aria-label="sort by"
              data-testid="current-sort"
            >
              <ListItemText
                primary={(
                  <div className="sui-flex sui-items-center sui-justify-end sui-gap-2">
                    <Typography
                      color="subtle"
                      ref={popover.anchorRef}
                    >
                      Sort by
                    </Typography>
                    <span className="sui-hidden md:sui-block">
                      {getSortNameValue(
                        {
                          key: (sortBy && sortBy.toLowerCase()) || '',
                          order: (sortOrder && sortOrder.toLowerCase()) || '',
                        }).name}
                    </span>
                    <div className="sui-mt-1">
                      {
                        open
                          ? <ArrowUp size="small" />
                          : <ArrowDown size="small" />
                      }
                    </div>
                  </div>

                )}
              />
            </ListItemButton>
          </List>
          {shouldRenderPopoverComponent && (
            <Popover
              id={popover.isOpen ? 'sort-by-popover' : undefined}
              open={popover.isOpen}
              anchorEl={popover.anchorEl}
              onClose={handlePopoverClose}
              title="Need it faster?"
              placement="bottom-start"
              offset="snug"
              disableEnforceFocus
              disablePortal={false}
              className="!sui-z-1"
            >
              <Typography variant="body-sm">
                Sort by &quot;Delivery Speed&quot;
              </Typography>
            </Popover>
          )}
          <div className="sui-flex sui-justify-end">
            <Menu
              id="sort-menu"
              className="sui-z-tooltip"
              open={open}
              anchorEl={anchorRef.current}
              placement="bottom-end"
              onClose={handleDropdownClose}
              onScroll={handleScroll}
              data-testid="sort-dropdown"
              MenuListProps={{ 'aria-labelledby': 'basic-button' }}
            >
              {sortTypes.map((option, index) => (
                <MenuItem
                  key={option.name}
                  value={option.name}
                  selected={
                    getSortNameValue(
                      {
                        key: (sortBy && sortBy.toLowerCase()) || '',
                        order: (sortOrder && sortOrder.toLowerCase()) || '',
                      }).name === option.name
                  }
                  onClick={handleSortClick}
                  data-testid="sort-type"
                >
                  {option.name}
                </MenuItem>
              ))}
            </Menu>
          </div>
        </div>
      )}
      {drawer && (
        <>
          <Accordion key="sortFilterInMobile" noBorders data-component="ResultsSortByDrawer">
            <AccordionHeader aria-controls="sortFilterInMobile-content" id="sortFilterInMobile-header">
              <div className="sui-flex-auto sui-block">
                <Typography component="div" variant="body-lg" weight="bold" height="tight">Sort by:</Typography>
                <div className="sui-mt-1">
                  <Typography variant="body-base" color="subtle">
                    {sortName}
                  </Typography>
                </div>
              </div>
            </AccordionHeader>
            <AccordionBody id={`accordion-${sortName}-body`}>
              <FormController>
                <RadioGroup
                  aria-labelledby="sortBy-radio-buttons-group-label"
                  name="radio-buttons-group"
                >
                  <div className="sui-ml-1">
                    {generateSortByRadios(sortTypes)}
                  </div>
                </RadioGroup>
              </FormController>
            </AccordionBody>
          </Accordion>
          <div className="sui-w-full sui-border-primary sui-border-b-1 sui-border-solid" />
        </>
      )}
    </>
  );
};

ResultsSortBy.propTypes = {
  onSortChange: func.isRequired,
  drawer: bool,
  oldDesign: bool,
  isMobile: bool,
  searchModelData: PropTypes.shape({
    searchModel: PropTypes.shape({
      products: PropTypes.arrayOf(
        PropTypes.shape({
          identifiers: PropTypes.shape({
            productType: PropTypes.string,
          }),
        })
      ),
      searchReport: PropTypes.shape({
        startIndex: PropTypes.number,
        pageSize: PropTypes.number,
        keyword: PropTypes.string,
        sortBy: PropTypes.string,
        sortOrder: PropTypes.string,
      }),
    }),
  }),
};

ResultsSortBy.defaultProps = {
  drawer: false,
  oldDesign: false,
  isMobile: false,
  searchModelData: undefined
};

ResultsSortBy.displayName = 'ResultsSortBy';

export { ResultsSortBy };
